import type { AccountingOverviewData } from '../hooks/useAccountingData';

interface Props {
  data: AccountingOverviewData | null;
  loading: boolean;
}

function formatAmount(v: number): string {
  if (v >= 10000) return `¥${(v / 10000).toFixed(1)}万`;
  return `¥${v.toLocaleString('zh-CN')}`;
}

export default function AccountingOverview({ data, loading }: Props) {
  // 多收 = 客户预付下来的余额，默认不占格子，真有才显示
  const totalOverPayment = data?.totalOverPayment ?? 0;
  const cards = [
    {
      label: '累计总收入',
      value: loading ? '-' : formatAmount(data?.totalIncome ?? 0),
      sub: loading ? '' : `本月 ${formatAmount(data?.thisMonthIncome ?? 0)}`,
      icon: 'ri-money-dollar-circle-line',
      bg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      valueColor: 'text-gray-900',
    },
    {
      label: '累计总支出',
      value: loading ? '-' : formatAmount(data?.totalExpense ?? 0),
      sub: loading ? '' : `本月 ${formatAmount(data?.thisMonthExpense ?? 0)}`,
      icon: 'ri-arrow-up-circle-line',
      bg: 'bg-rose-50',
      iconColor: 'text-rose-500',
      valueColor: 'text-rose-600',
    },
    {
      label: '累计净利润',
      value: loading ? '-' : formatAmount(data?.netProfit ?? 0),
      sub: loading ? '' : `本月 ${formatAmount(data?.thisMonthProfit ?? 0)}`,
      icon: 'ri-funds-line',
      bg: 'bg-teal-50',
      iconColor: 'text-teal-600',
      valueColor: data && data.netProfit < 0 ? 'text-rose-500' : 'text-teal-600',
    },
    {
      label: '已收款',
      value: loading ? '-' : formatAmount(data?.totalPaid ?? 0),
      sub: loading ? '' : `占收入 ${data && data.totalIncome > 0 ? Math.round((data.totalPaid / data.totalIncome) * 100) : 0}%`,
      icon: 'ri-secure-payment-line',
      bg: 'bg-cyan-50',
      iconColor: 'text-cyan-600',
      valueColor: 'text-gray-900',
    },
    {
      label: '待收款',
      value: loading ? '-' : formatAmount(data?.totalPending ?? 0),
      sub: loading ? '' : `${data && data.totalIncome > 0 ? Math.round((data.totalPending / data.totalIncome) * 100) : 0}% 未收`,
      icon: 'ri-hand-coin-line',
      bg: data && (data.totalPending ?? 0) > 0 ? 'bg-amber-50' : 'bg-gray-50',
      iconColor: data && (data.totalPending ?? 0) > 0 ? 'text-amber-600' : 'text-gray-400',
      valueColor: data && (data.totalPending ?? 0) > 0 ? 'text-amber-600' : 'text-gray-500',
    },
    ...(totalOverPayment > 0
      ? [
          {
            label: '多收',
            value: formatAmount(totalOverPayment),
            sub: '预收余额，不冲抵待收',
            icon: 'ri-stack-line',
            bg: 'bg-sky-50',
            iconColor: 'text-sky-600',
            valueColor: 'text-sky-600',
          },
        ]
      : []),
  ];

  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${
      cards.length > 5 ? 'lg:grid-cols-6' : 'lg:grid-cols-5'
    }`}>
      {cards.map(card => (
        <div key={card.label} className="bg-white border border-gray-100 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className={`w-9 h-9 ${card.bg} rounded-xl flex items-center justify-center`}>
              <i className={`${card.icon} text-base ${card.iconColor}`} />
            </div>
          </div>
          <p className={`text-xl font-bold leading-none mb-1.5 truncate ${loading ? 'animate-pulse text-gray-200' : card.valueColor}`}>
            {card.value}
          </p>
          <p className="text-xs text-gray-400 truncate">{card.label}</p>
          {card.sub && <p className="text-xs text-gray-300 truncate mt-0.5">{card.sub}</p>}
        </div>
      ))}
    </div>
  );
}
